import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/Card";
import { saveSession } from "@/lib/session";
import { SUPABASE_URL } from "@/lib/config";
import { getOverview } from "@/lib/api";

/**
 * Handoff entry point from the Roverpass platform.
 *
 * The expected URL — produced by the `auth-exchange` edge function and
 * returned to Roverpass for the redirect — is:
 *
 *   https://chatbot.roverpass.com/handoff?token=<plaintext>&redirect=<path>
 *
 * The token is read from the URL on mount, persisted as a bearer-mode
 * session, and the address bar is cleaned up so the token doesn't linger
 * in the user's history. Then we call get_overview once to verify the
 * token actually works before letting them in.
 *
 * Both query and fragment params are accepted. Query is the default Roverpass
 * shape; fragment is supported for callers who want to keep the token out of
 * server-side access logs. If both are present, fragment wins.
 *
 * Optional params
 *   url       override the Supabase project URL (multi-cluster setups)
 *   slug      shown in the sidebar; identity actually comes from the token
 *   redirect  path to land on after the handoff completes (default "/")
 */
export function Handoff() {
  const nav = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      // Fragment wins over query — same key in either spot.
      const fragment = parseParams(window.location.hash);
      const query    = parseParams(location.search);
      const get = (k: string) => fragment.get(k) ?? query.get(k);

      const token = get("token");
      if (!token) {
        setError("Missing token in the handoff URL. Try opening the dashboard again from Roverpass.");
        return;
      }
      const url = get("url") || SUPABASE_URL;
      if (!url) {
        setError("Missing Supabase URL — set VITE_SUPABASE_URL or include `url` in the handoff URL.");
        return;
      }
      const redirect = sanitizeRedirect(get("redirect")) ?? "/";

      saveSession({
        url,
        token,
        mode: "bearer",
        slug: get("slug") || undefined,
      });

      try {
        await getOverview();
        // Strip the token from history. Use replaceState so the back button
        // doesn't re-trigger the handoff (which would now fail because the
        // server-side token was already consumed in the URL).
        window.history.replaceState(null, "", redirect);
        nav(redirect, { replace: true });
      } catch (e) {
        localStorage.removeItem("roverpass-chatbot-dashboard");
        setError(
          e instanceof Error
            ? `The handoff token was rejected: ${e.message}`
            : "The handoff token was rejected.",
        );
      }
    })();
  }, [nav, location.search]);

  return (
    <div className="grid h-screen w-screen place-items-center bg-ink px-4">
      <div className="w-full max-w-md">
        <Logo className="mx-auto mb-6 h-9 [&_svg]:h-full [&_svg]:w-auto" />
        <Card className="bg-surface">
          <div className="px-5 py-6 text-center">
            {error ? (
              <div className="space-y-3">
                <div className="mx-auto flex h-9 w-9 items-center justify-center rounded bg-danger-soft text-danger">
                  <AlertCircle size={18} strokeWidth={1.75} />
                </div>
                <p className="text-sm font-medium text-text-strong">Handoff failed</p>
                <p className="text-sm text-text-soft">{error}</p>
                <p className="text-xs text-text-faint">
                  Re-open the dashboard from your Roverpass account to try again.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="mx-auto h-9 w-9 animate-pulse rounded-full bg-coral-soft" />
                <p className="text-sm font-medium text-text-strong">Signing you in…</p>
                <p className="text-xs text-text-soft">Verifying your dashboard token.</p>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

// Parses either `?a=b&c=d` or `#a=b&c=d` into URLSearchParams.
function parseParams(raw: string): URLSearchParams {
  if (!raw) return new URLSearchParams();
  const clean = raw.startsWith("#") || raw.startsWith("?") ? raw.slice(1) : raw;
  return new URLSearchParams(clean);
}

// Reject anything that's not an in-app relative path. Same logic as the
// auth-exchange edge function — prevents open-redirect via `redirect=`.
function sanitizeRedirect(input: string | null | undefined): string | null {
  if (!input) return null;
  const v = input.trim();
  if (!v.startsWith("/") || v.startsWith("//")) return null;
  return v;
}
